import * as React from "react"
import { Loader2 } from "lucide-react"

import { SongCard } from "@/components/song-card"
import type { Song } from "@/types/song"

interface SongGridProps {
  songs: Song[]
  favorites: ReadonlySet<number>
  onToggleFavorite: (id: number) => void
  onReachEnd?: () => void
  isLoadingMore?: boolean
}

export function SongGrid({
  songs,
  favorites,
  onToggleFavorite,
  onReachEnd,
  isLoadingMore,
}: Readonly<SongGridProps>) {
  const sentinelRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!onReachEnd) return
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onReachEnd()
        }
      },
      { rootMargin: "600px" }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [onReachEnd])

  if (songs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-24 text-center text-muted-foreground">
        <p className="text-lg font-medium text-foreground">찾는 곡이 없어요</p>
        <p className="text-sm">
          검색어나 필터를 바꿔보거나, 방송 채팅으로 신청곡을 제안해보세요!
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {songs.map((song) => (
          <SongCard
            key={song.id}
            song={song}
            isFavorite={favorites.has(song.id)}
            onToggleFavorite={onToggleFavorite}
          />
        ))}
      </div>
      <div ref={sentinelRef} className="h-1" aria-hidden />
      {isLoadingMore && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          더 불러오는 중...
        </div>
      )}
    </>
  )
}
